import React, { useState, useEffect } from 'react';
import { groupBy } from 'lodash';

import EventTracking from 'lib/EventTracking';
import { coachingHomeEventPrefix } from 'coaching/components/CoachingHomeView/CoachingHomeView';

import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import CancelRecurringSession from 'coaching/components/CoachingHomeView/CancelRecurringSession';
import CoachingConstants from 'coaching/CoachingConstants';
import {
  CoachSidebarHeader,
  CoachingSessionScheduleRow,
} from 'coaching/components/CoachingHomeView/ManageRecurringSessionsRows';
import ConfirmCancelledRecurringSessionOrInstance from 'coaching/components/CoachingHomeView/ConfirmCancelledRecurringSessionOrInstance';
import Divider from 'components/Divider';
import Icon from 'components/Icon';
import ManageRecurringSession from 'coaching/components/CoachingHomeView/ManageRecurringSession';
import MediaObject from 'components/MediaObject';
import SidebarFooter from 'components/SidebarFooter';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';

import { wouldBeLateCancellation } from 'coaching/components/CoachingHomeView/CoachingSessionInstanceRowComponents';
import { deleteCoachingSession } from 'coaching/actions/CoachingActions';
import GET_COACHING_HOME_DASHBOARD_QUERY from 'coaching/queries/GetCoachingHomeDashboard.gql';
import { CoachingSessionScheduleCancellationReasons } from 'coaching/components/CoachingHomeView/CancelRecurringSession';
import {
  CoachingSessionSchedule,
  CoachingSessionInstance,
} from 'coaching/components/CoachingHomeView/CoachingSessionsBySubjectContainer';

import 'coaching/components/styles/ManageRecurringSessionSidebar.scss';

type CancellationSteps =
  | 'select-schedule'
  | 'manage-plan'
  | 'cancel-recurring-session'
  | 'confirmation';

type Props = {
  participantUuid: string;
  recurringCoachingSessionSchedules: CoachingSessionSchedule[];
  upcomingInstances?: CoachingSessionInstance[];
  subjectName: string;
  onClose: () => void;
  onClickBookSession: () => void;
};

export default function ManageRecurringSessionSidebar({
  participantUuid,
  recurringCoachingSessionSchedules,
  upcomingInstances,
  subjectName,
  onClose,
  onClickBookSession,
}: Props) {
  const hasMultipleSchedules = recurringCoachingSessionSchedules.length > 1;
  const defaultStep = hasMultipleSchedules ? 'select-schedule' : 'manage-plan';
  const defaultCoachingSessionSchedule = hasMultipleSchedules
    ? undefined
    : recurringCoachingSessionSchedules[0];

  const [step, setStep] = useState<CancellationSteps>(defaultStep);
  const [submitting, setSubmitting] = useState(false);
  const [cancellationReason, setCancellationReason] = useState<
    CoachingSessionScheduleCancellationReasons
  >();
  const [cancellationDetails, setCancellationDetails] = useState<string>();
  const [coachingSessionSchedule, setCoachingSessionSchedule] = useState<
    CoachingSessionSchedule | undefined
  >(defaultCoachingSessionSchedule);

  const coachName = coachingSessionSchedule?.coach.name;
  const nextInstanceOfSelectedSchedule = upcomingInstances?.find(
    instance => instance.coachingSessionScheduleUuid === coachingSessionSchedule?.uuid
  );
  const isLateCancellation =
    nextInstanceOfSelectedSchedule &&
    wouldBeLateCancellation(nextInstanceOfSelectedSchedule.startDateTime);

  function getEventComponentName() {
    switch (step) {
      case 'manage-plan':
        return 'manage_plan_sidebar';

      case 'cancel-recurring-session':
      case 'confirmation':
        return 'cancel_plan_sidebar';
    }
  }
  const eventPrefix = `${coachingHomeEventPrefix}.${getEventComponentName()}`;
  function trackKey(key: string) {
    EventTracking.trackKey(`${eventPrefix}.${key}`);
  }

  useEffect(() => {
    trackKey('view');
  }, []);

  function resetCancellationExplanation() {
    setCancellationReason(undefined);
    setCancellationDetails(undefined);
  }

  useEffect(() => {
    // reset state if we change the participant/subject
    setStep(defaultStep);
    resetCancellationExplanation();
    setCoachingSessionSchedule(defaultCoachingSessionSchedule);
  }, [participantUuid, subjectName]);

  // reset the cancellation explanation when the user
  // goes back to the manage-plan step
  useEffect(() => {
    resetCancellationExplanation();
  }, [step === 'manage-plan']);

  const onCancellationSuccess = () => {
    setStep('confirmation');
  };

  async function handleSubmit() {
    if (!isLateCancellation && cancellationReason && coachingSessionSchedule) {
      setSubmitting(true);
      await deleteCoachingSession(
        coachingSessionSchedule.uuid,
        cancellationReason,
        undefined,
        undefined,
        [
          {
            query: GET_COACHING_HOME_DASHBOARD_QUERY,
            variables: {},
          },
        ],
        onCancellationSuccess
      );
      setSubmitting(false);
    } else if (isLateCancellation && coachingSessionSchedule) {
      setSubmitting(true);
      await deleteCoachingSession(
        coachingSessionSchedule.uuid,
        CoachingConstants.SELF_SERVE_LATE_CANCELLATION_REASON,
        undefined,
        undefined,
        [
          {
            query: GET_COACHING_HOME_DASHBOARD_QUERY,
            variables: {},
          },
        ],
        onCancellationSuccess
      );
      setSubmitting(false);
    } else {
      ToastActions.showErrorToast(
        'Oops! Looks like you tried to delete a session without specifying a cancellation reason. Please try again.'
      );
    }
  }

  const stepToHeaderMap: Record<CancellationSteps, React.ReactElement> = {
    'select-schedule': <Text color="graphite">Manage Plan</Text>,
    'manage-plan': (
      <>
        {hasMultipleSchedules && (
          <Button
            data-test="back-to-select-schedule"
            level="icon"
            color="chalkboard"
            onClick={() => setStep('select-schedule')}>
            <Icon name="arrow-left" />
          </Button>
        )}
        <Text alignWithIcon={hasMultipleSchedules} color="graphite">
          Manage Plan
        </Text>
      </>
    ),
    'cancel-recurring-session': (
      <>
        <Button
          eventKey={`${eventPrefix}.back.click`}
          level="icon"
          color="chalkboard"
          onClick={() => setStep('manage-plan')}>
          <Icon name="arrow-left" />
        </Button>
        <Text alignWithIcon color="graphite">
          Cancel Plan
        </Text>
      </>
    ),
    confirmation: <Text color="graphite">Cancel Plan</Text>,
  };

  const stepToSubHeaderMap: Record<CancellationSteps, React.ReactNode> = {
    'select-schedule': null,
    'manage-plan': <CoachSidebarHeader subjectName={subjectName} coachName={coachName} />,
    'cancel-recurring-session': null,
    confirmation: null,
  };

  // The key here comes back as a concatenated string - "<coachUuid>,<coachName>"
  const schedulesGroupedByCoach: Record<
    string,
    CoachingSessionSchedule[]
  > = groupBy(recurringCoachingSessionSchedules, schedule => [
    schedule.coach.uuid,
    schedule.coach.name,
  ]);

  const stepToComponentMap: Record<CancellationSteps, React.ReactElement> = {
    'select-schedule': (
      <div data-test="select-schedule-step">
        {Object.entries(schedulesGroupedByCoach).map(([coachUuidNameKey, schedules]) => {
          return (
            <div
              key={coachUuidNameKey}
              className="ManageRecurringSessionSidebar-sessionScheduleRow">
              <CoachSidebarHeader
                coachName={coachUuidNameKey.split(',')[1]}
                subjectName={subjectName}
              />
              {schedules.map(schedule => {
                return (
                  <div
                    data-test={`coaching-session-schedule-row-${schedule.uuid}`}
                    key={schedule.uuid}
                    onClick={() => {
                      setCoachingSessionSchedule(schedule);
                      setStep('manage-plan');
                    }}>
                    <CoachingSessionScheduleRow
                      data-test="coaching-session-schedule-row"
                      schedule={schedule}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    ),
    'manage-plan': (
      <ManageRecurringSession
        data-test="manage-plan-step"
        eventPrefix={eventPrefix}
        onCancelPlan={() => setStep('cancel-recurring-session')}
      />
    ),
    'cancel-recurring-session': (
      <>
        <CancelRecurringSession
          data-test="cancel-recurring-session-step"
          eventPrefix={eventPrefix}
          coachName={coachName}
          wouldBeLateCancellation={isLateCancellation}
          onSelectCancellationReason={setCancellationReason}
          onChangeCancellationDetails={setCancellationDetails}
        />
        <SidebarFooter>
          <BoxBody size="small">
            <Button
              full
              color="apple"
              data-test="cancel-plan"
              eventKey={`${eventPrefix}.cancel_plan.click`}
              eventProperties={
                cancellationDetails && {
                  data: cancellationDetails,
                }
              }
              disabled={(!cancellationReason && !isLateCancellation) || submitting}
              loading={submitting}
              onClick={handleSubmit}>
              Cancel plan
            </Button>
          </BoxBody>
        </SidebarFooter>
      </>
    ),
    confirmation: (
      <ConfirmCancelledRecurringSessionOrInstance
        data-test="confirm-cancelled-plan-step"
        eventPrefix={eventPrefix}
        onClickBookSession={onClickBookSession}
      />
    ),
  };

  return (
    <>
      <BoxBody size="xsmall">
        <MediaObject figure="right">
          {stepToHeaderMap[step]}
          <Button
            eventKey={`${eventPrefix}.close.click`}
            level="icon"
            color="chalkboard"
            onClick={onClose}>
            <Icon name="x" />
          </Button>
        </MediaObject>
      </BoxBody>
      <Divider size="flush" />
      {stepToSubHeaderMap[step]}
      {stepToComponentMap[step]}
    </>
  );
}
